import React, { useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';

const DEFAULT_SIZE = 180;
const DEFAULT_INNER_RATIO = 0.65;

export interface DonutChartItem {
  label: string;
  value: number;
  color: string;
  ariaLabel: string;
  tooltipHtml: string;
}

interface DonutChartProps {
  items: DonutChartItem[];
  size?: number;
  innerRadiusRatio?: number;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  items,
  size = DEFAULT_SIZE,
  innerRadiusRatio = DEFAULT_INNER_RATIO,
}) => {
  const tooltip = useChartTooltip();
  const [hovered, setHovered] = useState<number | null>(null);

  const outerRadius = size / 2;
  const innerRadius = outerRadius * innerRadiusRatio;

  const data = useMemo(() => {
    const allZero = items.every((d) => d.value === 0);
    return allZero
      ? [{ label: 'No Data', value: 1, color: '#E0E0E0', ariaLabel: '', tooltipHtml: '' }]
      : items;
  }, [items]);

  const arcs = useMemo(() => {
    const pie = d3
      .pie<DonutChartItem>()
      .value((d) => d.value)
      .sort(null);
    const arc = d3
      .arc<d3.PieArcDatum<DonutChartItem>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius);
    return pie(data).map((d) => ({ ...d, path: arc(d) || '' }));
  }, [data, innerRadius, outerRadius]);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`translate(${size / 2},${size / 2})`}>
        {arcs.map((d, i) => {
          const isNoData = d.data.label === 'No Data';
          return (
            <path
              key={d.data.label}
              d={d.path}
              fill={d.data.color}
              stroke="#fff"
              strokeWidth={2}
              role="img"
              aria-label={d.data.ariaLabel}
              tabIndex={isNoData ? -1 : 0}
              style={{ cursor: isNoData ? 'default' : 'pointer' }}
              opacity={hovered === i ? 0.8 : 1}
              onMouseEnter={(e) => {
                if (isNoData) return;
                setHovered(i);
                tooltip.show(e, d.data.tooltipHtml);
              }}
              onMouseMove={(e) => {
                if (isNoData) return;
                tooltip.move(e);
              }}
              onMouseLeave={() => {
                setHovered(null);
                tooltip.hide();
              }}
              onFocus={(e) => {
                if (isNoData) return;
                setHovered(i);
                tooltip.show(e, d.data.tooltipHtml);
              }}
              onBlur={() => {
                setHovered(null);
                tooltip.hide();
              }}
            />
          );
        })}
      </g>
    </svg>
  );
};
